import { Formik } from 'formik';
import { useCallback } from 'react';
import { object } from 'yup';

import type {
  Hole,
  DepthType,
  ToleranceType,
  Value,
  Tolerance,
} from '@frontend/api';
import {
  DEPTH_TYPES,
  RUNES_CALLOUT_TYPES,
  TOLERANCE_TYPES,
} from '@frontend/api';
import {
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  GDTSymbolText,
  Flex,
} from '@frontend/components';

import { SubFormProps } from '../types';
import { FormPreviewSection } from './shared/FormPreviewSection';
import { MultiplierInput } from './shared/MultiplierInput';
import { NumberValueField } from './shared/NumberInputValue';
import { SubFormWrapper } from './shared/SubFormWrapper';
import { ToleranceInputGroup } from './shared/ToleranceInputGroup';
import { DepthTypeSelect } from './shared/TypedSelects';
import {
  createRequiredStringNumberField,
  createBooleanField,
  createStringToleranceValidationGroup,
  createMultiplierValidationGroup,
  createDepthTypeValidation,
  createStringDepthValueValidation,
  createStringDepthToleranceValidationGroup,
  parsePrecisionValue,
} from './shared/validation';
import {
  buildPrecisionMeasurementObject,
  stringToleranceFormValuesToConfig,
  extractMultiplier,
} from './shared/valueBuilders';

type HoleFormValues = {
  diameter: string;
  hasTolerance: boolean;
  toleranceType: ToleranceType;
  toleranceUpper?: string;
  toleranceLower?: string;
  toleranceSymmetric?: string;
  hasMult: boolean;
  mult?: number;
  hasDepth: boolean;
  depthType: DepthType;
  depthValue?: string;
  depthHasTolerance: boolean;
  depthToleranceType: ToleranceType;
  depthToleranceUpper?: string;
  depthToleranceLower?: string;
  depthToleranceSymmetric?: string;
};

const initialValues: HoleFormValues = {
  diameter: '0',
  hasTolerance: false,
  toleranceType: TOLERANCE_TYPES.SYMMETRIC,
  toleranceUpper: undefined,
  toleranceLower: undefined,
  toleranceSymmetric: undefined,
  hasMult: false,
  mult: undefined,
  hasDepth: false,
  depthType: DEPTH_TYPES.DEPTH,
  depthValue: undefined,
  depthHasTolerance: false,
  depthToleranceType: TOLERANCE_TYPES.SYMMETRIC,
  depthToleranceUpper: undefined,
  depthToleranceLower: undefined,
  depthToleranceSymmetric: undefined,
};

/**
 * Converts HoleFormValues into a concise string summary using appropriate symbols
 * @param values - The hole form values to convert
 * @returns A formatted string representation of the hole
 */
const formatHoleSummary = (values: HoleFormValues): string => {
  let summary = '';

  // Add multiplier first if present
  if (values.hasMult && values.mult !== undefined) {
    summary += `${values.mult}×`;
  }

  // Add diameter with symbol
  summary += `⌀${values.diameter}`;

  // Add diameter tolerance if present
  if (values.hasTolerance) {
    switch (values.toleranceType) {
      case TOLERANCE_TYPES.SYMMETRIC:
        if (values.toleranceSymmetric) {
          summary += ` ±${values.toleranceSymmetric}`;
        }
        break;
      case TOLERANCE_TYPES.ASYMMETRIC:
        if (values.toleranceUpper && values.toleranceLower) {
          summary += ` +${values.toleranceUpper}/-${values.toleranceLower}`;
        }
        break;
      case TOLERANCE_TYPES.LIMIT:
        if (values.toleranceUpper && values.toleranceLower) {
          summary += ` [${values.toleranceLower}, ${values.toleranceUpper}]`;
        }
        break;
    }
  }

  // Add depth information if present
  if (values.hasDepth) {
    if (values.depthType === DEPTH_TYPES.THRU) {
      summary += ' THRU';
    } else if (values.depthType === DEPTH_TYPES.DEPTH && values.depthValue) {
      summary += ` ↧${values.depthValue}`;

      // Add depth tolerance if present
      if (values.depthHasTolerance) {
        switch (values.depthToleranceType) {
          case TOLERANCE_TYPES.SYMMETRIC:
            if (values.depthToleranceSymmetric) {
              summary += ` ±${values.depthToleranceSymmetric}`;
            }
            break;
          case TOLERANCE_TYPES.ASYMMETRIC:
            if (values.depthToleranceUpper && values.depthToleranceLower) {
              summary += ` +${values.depthToleranceUpper}/-${values.depthToleranceLower}`;
            }
            break;
          case TOLERANCE_TYPES.LIMIT:
            if (values.depthToleranceUpper && values.depthToleranceLower) {
              summary += ` [${values.depthToleranceLower}, ${values.depthToleranceUpper}]`;
            }
            break;
        }
      }
    }
  }

  return summary;
};

const validationSchema = object().shape({
  diameter: createRequiredStringNumberField('Diameter'),
  ...createStringToleranceValidationGroup(),
  ...createMultiplierValidationGroup(),
  hasDepth: createBooleanField(),
  depthType: createDepthTypeValidation('hasDepth'),
  depthValue: createStringDepthValueValidation(),
  ...createStringDepthToleranceValidationGroup(),
});

export const HoleForm = ({
  onSubmit,
  onCancel,
  renderSelectedType,
}: SubFormProps) => {
  const handleSubmit = useCallback(
    (values: HoleFormValues) => {
      // Parse diameter value for tolerance calculations
      const parsedDiameter = parsePrecisionValue(values.diameter);
      if (!parsedDiameter) {
        return;
      }

      // Build diameter measurement with tolerance
      const diameterToleranceConfig = stringToleranceFormValuesToConfig(
        {
          hasTolerance: values.hasTolerance,
          toleranceType: values.toleranceType,
          toleranceSymmetric: values.toleranceSymmetric,
          toleranceUpper: values.toleranceUpper,
          toleranceLower: values.toleranceLower,
        },
        parsedDiameter.numericValue,
      );

      const dia = buildPrecisionMeasurementObject(
        values.diameter,
        diameterToleranceConfig,
      );
      if (!dia) {
        return;
      }

      // Build depth measurement if enabled
      let depth: { value: Value; tol?: Tolerance } | undefined;
      if (
        values.hasDepth &&
        values.depthType === DEPTH_TYPES.DEPTH &&
        values.depthValue
      ) {
        const parsedDepth = parsePrecisionValue(values.depthValue);
        if (!parsedDepth) {
          return;
        }

        const depthToleranceConfig = stringToleranceFormValuesToConfig(
          {
            hasTolerance: values.depthHasTolerance,
            toleranceType: values.depthToleranceType,
            toleranceSymmetric: values.depthToleranceSymmetric,
            toleranceUpper: values.depthToleranceUpper,
            toleranceLower: values.depthToleranceLower,
          },
          parsedDepth.numericValue,
        );

        depth = buildPrecisionMeasurementObject(
          values.depthValue,
          depthToleranceConfig,
        );
        if (!depth) {
          return;
        }
      }

      // Build the final Hole object
      const hole: Hole = {
        calloutType: RUNES_CALLOUT_TYPES.HOLE,
        mult: extractMultiplier({ hasMult: values.hasMult, mult: values.mult }),
        dia,
        depthType: values.hasDepth ? values.depthType : undefined,
        depth,
      };

      onSubmit(hole, formatHoleSummary(values));
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => (
        <SubFormWrapper
          isValid={isValid}
          isSubmitting={isSubmitting}
          onCancel={onCancel}
          renderSelectedType={renderSelectedType}
          submitLabel="Add Hole"
        >
          {/* Preview Section */}
          <FormPreviewSection>
            <GDTSymbolText size="md">{formatHoleSummary(values)}</GDTSymbolText>
          </FormPreviewSection>

          {/* Form Fields */}
          <Flex direction="column" gap={1} px={1} pb={1}>
            {/* Diameter Value */}
            <FormControl>
              <FormLabel>Diameter</FormLabel>
              <NumberValueField
                name="diameter"
                value={values.diameter}
                onChange={(value) => setFieldValue('diameter', value)}
                size="sm"
                min={0}
              />
              {errors.diameter && touched.diameter && (
                <FormErrorMessage>{errors.diameter}</FormErrorMessage>
              )}
            </FormControl>

            {/* Diameter Tolerance */}
            <ToleranceInputGroup
              hasToleranceFieldName="hasTolerance"
              hasTolerance={values.hasTolerance}
              onHasToleranceChange={(checked) =>
                setFieldValue('hasTolerance', checked)
              }
              toleranceTypeFieldName="toleranceType"
              toleranceType={values.toleranceType}
              onToleranceTypeChange={(type) =>
                setFieldValue('toleranceType', type)
              }
              symmetricFieldName="toleranceSymmetric"
              symmetricValue={values.toleranceSymmetric}
              onSymmetricChange={(value) =>
                setFieldValue('toleranceSymmetric', value)
              }
              upperFieldName="toleranceUpper"
              upperValue={values.toleranceUpper}
              onUpperChange={(value) => setFieldValue('toleranceUpper', value)}
              lowerFieldName="toleranceLower"
              lowerValue={values.toleranceLower}
              onLowerChange={(value) => setFieldValue('toleranceLower', value)}
              errors={errors}
              touched={touched}
              checkboxLabel="Include diameter tolerance"
            />

            {/* Depth Checkbox and Inputs */}
            <Checkbox
              name="hasDepth"
              isChecked={values.hasDepth}
              onChange={(e) => setFieldValue('hasDepth', e.target.checked)}
              size="sm"
            >
              Include depth
            </Checkbox>
            {values.hasDepth && (
              <>
                {/* Depth Type Select */}
                <DepthTypeSelect
                  name="depthType"
                  label="Depth Type"
                  value={values.depthType}
                  onChange={(value) => setFieldValue('depthType', value)}
                  error={errors.depthType}
                  touched={touched.depthType}
                />

                {/* Depth Value Input (only for DEPTH type) */}
                {values.depthType === DEPTH_TYPES.DEPTH && (
                  <>
                    <FormControl>
                      <FormLabel>Depth Value</FormLabel>
                      <NumberValueField
                        name="depthValue"
                        value={values.depthValue || ''}
                        onChange={(value) => setFieldValue('depthValue', value)}
                        size="sm"
                        min={0}
                      />
                      {errors.depthValue && touched.depthValue && (
                        <FormErrorMessage>{errors.depthValue}</FormErrorMessage>
                      )}
                    </FormControl>

                    {/* Depth Tolerance */}
                    <ToleranceInputGroup
                      hasToleranceFieldName="depthHasTolerance"
                      hasTolerance={values.depthHasTolerance}
                      onHasToleranceChange={(checked) =>
                        setFieldValue('depthHasTolerance', checked)
                      }
                      toleranceTypeFieldName="depthToleranceType"
                      toleranceType={values.depthToleranceType}
                      onToleranceTypeChange={(type) =>
                        setFieldValue('depthToleranceType', type)
                      }
                      symmetricFieldName="depthToleranceSymmetric"
                      symmetricValue={values.depthToleranceSymmetric}
                      onSymmetricChange={(value) =>
                        setFieldValue('depthToleranceSymmetric', value)
                      }
                      upperFieldName="depthToleranceUpper"
                      upperValue={values.depthToleranceUpper}
                      onUpperChange={(value) =>
                        setFieldValue('depthToleranceUpper', value)
                      }
                      lowerFieldName="depthToleranceLower"
                      lowerValue={values.depthToleranceLower}
                      onLowerChange={(value) =>
                        setFieldValue('depthToleranceLower', value)
                      }
                      errors={errors}
                      touched={touched}
                      checkboxLabel="Include depth tolerance"
                      toleranceLabel="Depth Tolerance Type"
                      symmetricLabel="Depth Tolerance (±)"
                      upperLabel="Depth Upper Tolerance"
                      lowerLabel="Depth Lower Tolerance"
                    />
                  </>
                )}
              </>
            )}

            {/* Multiplier */}
            <MultiplierInput
              hasMultFieldName="hasMult"
              hasMult={values.hasMult}
              onHasMultChange={(checked) => setFieldValue('hasMult', checked)}
              multFieldName="mult"
              multValue={values.mult}
              onMultChange={(value) => setFieldValue('mult', value)}
              errors={errors}
              touched={touched}
            />
          </Flex>
        </SubFormWrapper>
      )}
    </Formik>
  );
};
